import React, { useEffect, useState } from 'react';
import { Coins, Coffee, Car, BedDouble, Beer } from 'lucide-react';
import { getCost } from '@/lib/api';

const TIER_META = {
  'very-cheap':     { label: 'Very cheap', color: 'from-emerald-400 to-cyan-400',  badge: '$',     dots: 1 },
  cheap:            { label: 'Cheap',      color: 'from-cyan-400 to-blue-500',     badge: '$$',    dots: 2 },
  moderate:         { label: 'Moderate',   color: 'from-blue-500 to-violet-500',   badge: '$$$',   dots: 3 },
  expensive:        { label: 'Expensive',  color: 'from-[#15291C] to-[#1F3A29]',badge: '$$$$',  dots: 4 },
  'very-expensive': { label: 'Pricey',     color: 'from-fuchsia-500 to-rose-500',  badge: '$$$$$', dots: 5 },
};

export default function CostEstimatorCard({ countryCode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!countryCode) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    getCost(countryCode).then(d => alive && setData(d)).catch(() => alive && setData(null)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [countryCode]);

  if (loading) return <div className="puvio-card p-5 gt-skeleton h-32" data-testid="cost-card-skeleton" />;
  if (!data || !data.available || !data.samples) return null;

  const t = TIER_META[data.tier] || TIER_META.moderate;
  const s = data.samples;
  const items = [
    { label: 'Meal',   value: `$${s.meal}`,   icon: Coffee, sub: 'mid range' },
    { label: 'Cab',    value: `$${s.cab_km}`, icon: Car,    sub: 'per km' },
    { label: 'Coffee', value: `$${s.coffee}`, icon: Coffee, sub: 'cappuccino' },
    { label: 'Hostel', value: `$${s.hostel}`, icon: BedDouble, sub: 'per night' },
  ];

  return (
    <div className="puvio-card p-5" data-testid="cost-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] flex items-center gap-1.5">
            <Coins className="w-3 h-3" /> Cost estimator
          </p>
          <p className="text-xs text-[#4A5A50] mt-0.5 font-medium">{data.country_code} · {t.label}</p>
        </div>
        <div className={`px-2.5 py-1 rounded-full text-[11px] font-bold text-[#0F1A13] bg-gradient-to-r ${t.color} shadow-[0_4px_14px_rgba(15,26,19,0.14)]`} data-testid="cost-tier-badge">
          {t.badge}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        {items.map((it, i) => (
          <div key={i} className="rounded-2xl bg-[#F2F0E4] border border-[#0F1A13]/06 px-3 py-2.5 flex items-center gap-2.5" data-testid={`cost-item-${it.label.toLowerCase()}`}>
            <it.icon className="w-3.5 h-3.5 text-[#15291C] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10.5px] uppercase tracking-wider text-[#7A8A80] font-semibold">{it.label}</p>
              <p className="text-sm font-bold text-[#0F1A13]">{it.value}<span className="text-[10.5px] text-[#7A8A80] font-medium ml-1">{it.sub}</span></p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10.5px] text-[#7A8A80] font-medium">
        <Beer className="w-3 h-3" /> Beer ${s.beer} · prices in USD, country-wide median
      </div>
    </div>
  );
}
